<template>
    <v-card class="pa-5" :loading="loading">
        <template v-if="!loading">
            <h2>{{ surveyTitle }}</h2>
            <v-progress-linear 
                :value="questionProgress" 
                color="black"
                style="border-radius: 10px;"
                height="15"
                class="mt-2"
            ></v-progress-linear>
            <span style="font-size: 0.8em; color: rgba(0,0,0,0.7); font-weight: bold;">paso {{questionStep}} de {{ questionSteps }}</span>
            <h3 class="mt-4">{{ question }}</h3>
            <v-text-field
                v-if="isOrderQuestion"
                placeholder="Ingrese el codigo de orden"
                v-model="orderIdManualInput"
                :rules="[v => !!v || 'El codigo de orden es requerido para continuar']"
            ></v-text-field>
            <v-autocomplete 
                v-else
                class="mt-4"
                :items="questionOptions"
                outlined dense
                placeholder="Seleccione una opción"
                :value="questionResponseValue"
                @input="updateQuestionResponseValue"
            ></v-autocomplete>
            <div class="d-flex flex-row justify-space-between">
                <v-btn v-if="isFirstStep && withCancel"
                    outlined
                    @click="cancelSurvey"
                >
                    <v-icon small left>mdi-cancel</v-icon>
                    {{ $t('common.cancel') }}
                </v-btn>
                <v-btn 
                    v-else
                    outlined :disabled="isFirstStep"
                    @click="previousQuestionStep"
                >
                    <v-icon small left>mdi-arrow-left</v-icon>
                    {{ $t('common.previous') }}
                </v-btn>
                <div class="mx-8"></div>
                <v-btn
                    v-if="isLastStep"
                    color="black" style="color: white;"
                    :disabled="nextButtonIsDisabled"
                    @click="finishSurveyResponse" :loading="finishSurveyResponseLoading"
                >
                    {{ $t('delivery.surveys.response.send') }}
                    <v-icon small right>mdi-checkbox-marked-circle-auto-outline</v-icon>
                </v-btn>
                <v-btn 
                    v-else
                    color="black" style="color: white;"
                    :disabled="nextButtonIsDisabled"
                    @click="nextQuestionStep"
                >
                    {{ $t('common.next') }}
                    <v-icon small right>mdi-arrow-right</v-icon>
                </v-btn>
            </div>
        </template>
    </v-card>
</template>

<script>
import SurveysProvider from '../../../delivery/providers/SurveysProvider'
import SurveyResponseProvider from '../../../delivery/providers/SurveyResponseProvider'

export default {
    name: 'SurveyResponse',
    props: {
        surveyId: {
            type: String,
            required: true
        },
        withOrderId: {
            type: Boolean,
            default: false
        },
        orderId: {
            type: String,
            required: false
        },
        withCancel: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            questionStep: 1,
            questionValues: [],
            survey: undefined,
            loading: true,
            orderIdManualInput: null,
            finishSurveyResponseLoading: false
        }
    },
    computed: {
        surveyTitle(){
            return this.survey?.name
        },
        questions(){
            const orderQuestion = []
            if(!this.withOrderId) orderQuestion.push({
                type: 'orderId',
                question: 'Ingrese el codigo de su orden a continuación'
            })
            return [...orderQuestion, ...this.survey?.questions] || []
        },
        questionProgress(){
            return this.questionStep * 100 / this.questions.length
        },
        questionSteps(){
            return this.questions.length
        },
        question(){
            return this.questions[this.questionIndex]?.question
        },
        isOrderQuestion(){
            return this.questions[this.questionIndex]?.type === 'orderId'
        },
        questionResponseValue(){
            return this.questionValues[this.questionIndex]
        },
        questionOptions(){
            return this.questions[this.questionIndex].options.map(option => option.value)
        },
        questionIndex(){
            return this.questionStep - 1
        },
        nextButtonIsDisabled(){
            if(!this.withOrderId && this.questionStep === 1 && !!this.orderIdManualInput) return false
            const questionValueIsUndefinedOrNull = !this.questionValues[this.questionIndex]
            return questionValueIsUndefinedOrNull
        },
        lastQuestionStep(){
            return this.questions.length
        },
        isLastStep(){
            return this.questionStep === this.lastQuestionStep
        },
        isFirstStep(){
            return this.questionStep === 1
        }
    },
    async mounted(){
        await this.loadSurvey()
        this.loadQuestionValuesWithUndefined()
    },
    methods: {
        resetComponent(){
            this.questionStep = 1
            this.questionValues = []
            this.orderIdManualInput = null

            this.loadQuestionValuesWithUndefined()
        },
        loadQuestionValuesWithUndefined(){
            this.questionValues = this.questions.map(() => undefined)
        },
        updateQuestionResponseValue(value){
            this.$set(this.questionValues, this.questionIndex, value)
        },
        nextQuestionStep(){
            this.questionStep ++
        },
        previousQuestionStep(){
            this.questionStep --
        },
        async finishSurveyResponse(){
            try{
                this.finishSurveyResponseLoading = true
                const surveyResponse = {
                    survey: this.survey.id,
                    order: this.withOrderId ? this.orderId : this.orderIdManualInput,
                    questions: this.questions.map((question, index) => {
                        if(question.type != 'orderId') return {
                            question: question.id,
                            value: this.questionValues[index]
                        }
                    }).filter(item => item != undefined)
                }
    
                await SurveyResponseProvider.createSurveyResponse(surveyResponse)

                this.$emit('send', surveyResponse) 
                this.resetComponent()
            } catch(error) {
                console.error('Error al enviar la encuesta respondida')
            } finally {
                this.finishSurveyResponseLoading = false
            }
        },
        cancelSurvey(){
            this.$emit('cancel')
            this.resetComponent()
        },
        async loadSurvey() {
            try {
                this.loading = true
                const { data: { findSurvey } } = await SurveysProvider.findSurvey(this.surveyId)
                this.survey = findSurvey
            } catch (error) {
                console.error(error)
            } finally {
                this.loading = false
            }
        }
    }
}
</script>